/**
 * Utilities for printing treatment plans
 */

import { getSurfaceName } from './dentalSurfaceUtils';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas-pro';
import { fetchImageAsDataUrl } from './imageUtils';

/**
 * Generates HTML content for printing a treatment plan
 * @param {Object} treatmentPlan - Treatment plan data
 * @param {Object} patient - Patient data
 * @param {Object} organization - Organization data (optional)
 * @param {Object} doctor - Doctor data (optional)
 * @param {Array|Object} procedureCatalog - Procedure catalog or map with tax configuration (optional)
 * @returns {string} HTML content for printing
 */
export const generateTreatmentPlanHTML = (treatmentPlan, patient, logoUrl = null, organization = null, doctor = null, procedureCatalog = null) => {
  const patientName = patient ? `${patient.firstName} ${patient.lastName}` : 'Paciente';
  const clinicName = organization?.name || doctor?.institution || 'Clínica Dental';
  const doctorName = treatmentPlan.doctorName || doctor?.name || 'Dr. Sin Asignar';
  const doctorTitle = doctor?.specialization || doctor?.title || 'Cirujano Dentista';
  const timezone = organization?.timezone || 'America/Mexico_City';
  const currentDate = new Date().toLocaleDateString('es-ES', { timeZone: timezone });
  const treatmentDate = treatmentPlan.date ? new Date(treatmentPlan.date).toLocaleDateString('es-ES', { timeZone: timezone }) : currentDate;
  const createdDate = treatmentPlan.createdAt ? new Date(treatmentPlan.createdAt).toLocaleDateString('es-ES', { timeZone: timezone }) : currentDate;
  const effectiveStatus = treatmentPlan.status === 'active' ? 'quoted' : treatmentPlan.status;
  const statusLabels = {
    quoted: 'Presupuestado',
    accepted: 'Aceptado',
    in_progress: 'En curso',
    paused: 'Pausado',
    completed: 'Completado',
    rejected: 'Rechazado'
  };

  const getStatusLabel = (status) => statusLabels[status] || 'Sin estado';

  const procedureCatalogMap = Array.isArray(procedureCatalog)
    ? procedureCatalog.reduce((acc, item) => {
      if (item?.id) {
        acc[item.id] = item;
      }
      return acc;
    }, {})
    : (procedureCatalog || {});

  const getProcedureTaxes = (procedure) => {
    if (procedure?.taxes) return procedure.taxes;
    const catalogEntry = procedureCatalogMap?.[procedure?.procedureId];
    return catalogEntry?.taxes || null;
  };

  const formatTaxLabel = (taxConfig, label) => {
    if (!taxConfig?.enabled) {
      return null;
    }
    const typeLabel = taxConfig.type === 'retencion' ? 'Retención' : 'Traslado';
    if (taxConfig.rate === 'exento') {
      return `${label} (${typeLabel}): Exento`;
    }
    if (taxConfig.rate === null || taxConfig.rate === undefined || taxConfig.rate === '') {
      return null;
    }
    return `${label} (${typeLabel}): ${taxConfig.rate}%`;
  };

  const getLineTotal = (procedure) => Number(procedure.total || 0);

  const getTaxRates = (taxes) => {
    const ivaRate = taxes?.iva?.enabled && taxes.iva.rate !== 'exento' && taxes.iva.rate !== null && taxes.iva.rate !== undefined && taxes.iva.rate !== ''
      ? Number(taxes.iva.rate || 0) / 100
      : 0;
    const isrRate = taxes?.isr?.enabled && taxes.isr.rate !== 'exento' && taxes.isr.rate !== null && taxes.isr.rate !== undefined && taxes.isr.rate !== ''
      ? Number(taxes.isr.rate || 0) / 100
      : 0;
    return { ivaRate, isrRate };
  };

  const getLineBase = (procedure, taxes) => {
    const total = getLineTotal(procedure);
    const { ivaRate, isrRate } = getTaxRates(taxes);
    const denominator = 1 + ivaRate - isrRate;
    if (denominator <= 0) return total;
    return total / denominator;
  };

  const getLineListSubtotal = (procedure, taxes) => {
    const quantity = Math.max(Number(procedure.quantity || 0), 0);
    const unitPrice = Number(procedure.amount);

    if (Number.isFinite(unitPrice) && unitPrice > 0) {
      return quantity * unitPrice;
    }

    return getLineBase(procedure, taxes);
  };

  const getLineDiscount = (procedure, taxes) => {
    const listSubtotal = getLineListSubtotal(procedure, taxes);
    const discountedSubtotal = getLineBase(procedure, taxes);
    return Math.max(0, listSubtotal - discountedSubtotal);
  };

  const getLineTaxAmount = (procedure, taxConfig, taxes) => {
    if (!taxConfig?.enabled) return 0;
    if (taxConfig.rate === 'exento' || taxConfig.rate === null || taxConfig.rate === undefined || taxConfig.rate === '') return 0;
    const rate = Number(taxConfig.rate || 0) / 100;
    const base = Math.max(0, getLineBase(procedure, taxes));
    return base * rate;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  // Get selected teeth information
  const selectedTeethInfo = () => {
    // Primero verificar si hay dientes en treatmentPlan.selectedTeeth (formato antiguo)
    if (treatmentPlan.selectedTeeth && Object.keys(treatmentPlan.selectedTeeth).length > 0) {
      return Object.entries(treatmentPlan.selectedTeeth).map(([tooth, surfaces]) => {
        const surfacesList = Object.keys(surfaces).map(surface => getSurfaceName(tooth, surface)).join(', ');
        return `Diente ${tooth} (${surfacesList})`;
      }).join(', ');
    }

    // Si no hay dientes en el formato antiguo, buscar en los procedimientos
    if (treatmentPlan.selectedProcedures && treatmentPlan.selectedProcedures.length > 0) {
      const allTeeth = {};

      // Recopilar todos los dientes de todos los procedimientos
      treatmentPlan.selectedProcedures.forEach(procedure => {
        if (procedure.selectedTeeth) {
          Object.entries(procedure.selectedTeeth).forEach(([tooth, surfaces]) => {
            if (!allTeeth[tooth]) {
              allTeeth[tooth] = {};
            }
            Object.entries(surfaces).forEach(([surface, color]) => {
              allTeeth[tooth][surface] = color;
            });
          });
        }
      });

      if (Object.keys(allTeeth).length === 0) {
        return 'No se han seleccionado dientes específicos';
      }

      return Object.entries(allTeeth).map(([tooth, surfaces]) => {
        const surfacesList = Object.keys(surfaces).map(surface => getSurfaceName(tooth, surface)).join(', ');
        return `Diente ${tooth} (${surfacesList})`;
      }).join(', ');
    }

    return 'No se han seleccionado dientes específicos';
  };

  // Get procedures list
  const proceduresList = () => {
    if (!treatmentPlan.selectedProcedures || treatmentPlan.selectedProcedures.length === 0) {
      return '<tr><td colspan="6" class="empty-row">No se han agregado procedimientos</td></tr>';
    }

    return treatmentPlan.selectedProcedures.map(procedure => {
      const procedureName = procedure.name || procedure.procedureName || 'Procedimiento';
      // Obtener información de dientes para este procedimiento
      const procedureTeeth = procedure.selectedTeeth ? Object.entries(procedure.selectedTeeth).map(([tooth, surfaces]) => {
        const surfacesList = Object.keys(surfaces).map(surface => getSurfaceName(tooth, surface)).join(', ');
        return `Diente ${tooth} (${surfacesList})`;
      }).join(', ') : 'No especificado';

      const taxes = getProcedureTaxes(procedure) || {};
      const ivaLabel = formatTaxLabel(taxes.iva, 'IVA');
      const isrLabel = formatTaxLabel(taxes.isr, 'ISR');
      const ivaAmount = getLineTaxAmount(procedure, taxes.iva, taxes);
      const isrAmount = getLineTaxAmount(procedure, taxes.isr, taxes);
      const lineListSubtotal = getLineListSubtotal(procedure, taxes);
      const lineTotal = getLineTotal(procedure);
      const lineDiscount = getLineDiscount(procedure, taxes);
      const quantity = Math.max(Number(procedure.quantity) || 1, 1);
      const lineUnitPrice = lineListSubtotal / quantity;

      return `
        <tr class="procedure-row">
          <td>
            <div class="procedure-name">${procedureName}</div>
            <div class="procedure-sub">Dientes: ${procedureTeeth}</div>
            ${procedure.notes ? `<div class="procedure-notes">Notas: ${procedure.notes}</div>` : ''}
          </td>
          <td class="text-center">${procedure.quantity}</td>
          <td class="text-right">${formatCurrency(lineUnitPrice)}</td>
          <td class="text-right">${formatCurrency(lineListSubtotal)}</td>
          <td>
            <div class="tax-lines">
              ${ivaLabel ? `<div class="tax-line"><span>${ivaLabel}</span><span>${formatCurrency(ivaAmount)}</span></div>` : ''}
              ${isrLabel ? `<div class="tax-line"><span>${isrLabel}</span><span>-${formatCurrency(isrAmount)}</span></div>` : ''}
              ${!ivaLabel && !isrLabel ? '<span class="muted">—</span>' : ''}
            </div>
          </td>
          <td class="text-right">${formatCurrency(lineTotal)}</td>
        </tr>
        ${lineDiscount > 0 ? `
          <tr class="procedure-discount">
            <td colspan="5" class="text-right">Descuento aplicado</td>
            <td class="text-right">-${formatCurrency(lineDiscount)}</td>
          </tr>
        ` : ''}
      `;
    }).join('');
  };

  const proceduresTotals = Array.isArray(treatmentPlan.selectedProcedures)
    ? treatmentPlan.selectedProcedures.reduce((acc, procedure) => {
      const taxes = getProcedureTaxes(procedure) || {};
      const listSubtotal = getLineListSubtotal(procedure, taxes);
      const discount = getLineDiscount(procedure, taxes);
      const ivaAmount = getLineTaxAmount(procedure, taxes.iva, taxes);
      const isrAmount = getLineTaxAmount(procedure, taxes.isr, taxes);
      acc.subtotal += listSubtotal;
      acc.discount += discount;
      acc.iva += ivaAmount;
      acc.isr += isrAmount;
      acc.total += getLineTotal(procedure);
      return acc;
    }, { subtotal: 0, discount: 0, iva: 0, isr: 0, total: 0 })
    : { subtotal: 0, discount: 0, iva: 0, isr: 0, total: 0 };

  // Get expiration info
  const getExpirationText = () => {
    if (!treatmentPlan.expirationDate) {
      return 'Vigencia: Sin fecha de vencimiento';
    }

    const expirationDate = new Date(treatmentPlan.expirationDate);
    const now = new Date();
    const diffTime = expirationDate - now;
    const computedRemainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const remainingDays = Number.isFinite(treatmentPlan.remainingDays)
      ? treatmentPlan.remainingDays
      : Math.max(0, computedRemainingDays);
    const isExpired = typeof treatmentPlan.isExpired === 'boolean'
      ? treatmentPlan.isExpired
      : expirationDate < now;

    if (isExpired) {
      return 'Vigencia: Cotizacion expirada';
    }

    return `Vigencia: ${remainingDays} dia${remainingDays !== 1 ? 's' : ''}`;
  };

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Plan de Tratamiento - ${patientName}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: "Helvetica Neue", Arial, sans-serif;
          margin: 12px;
          line-height: 1.35;
          color: #1a202c;
          background: #f7fafc;
        }

        .receipt {
          max-width: 780px;
          margin: 0 auto;
          background: #fff;
          border: 1px solid #e2e8f0;
          padding: 14px 16px;
          box-shadow: 0 2px 8px rgba(26, 32, 44, 0.06);
        }
        
        .treatment-plan-header {
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 8px;
          padding-bottom: 10px;
          border-bottom: 2px solid #2d3748;
          position: relative;
          min-height: 88px;
        }
        
        .logo-section {
          position: absolute;
          top: 5px;
          left: 0;
          width: 200px;
          height: 70px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          opacity: 0.8;
          z-index: 0;
        }

        .logo-section img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
          object-position: left center;
        }
        
        .header-content {
          width: 100%;
          text-align: center;
          z-index: 1;
        }
        
        .clinic-name {
          font-size: 15px;
          font-weight: bold;
          margin-bottom: 6px;
          text-transform: uppercase;
          color: #2d3748;
        }
        
        .document-title {
          font-size: 13px;
          font-weight: bold;
          color: #2d3748;
          margin: 6px 0;
          text-transform: uppercase;
          letter-spacing: 0.6px;
        }

        .receipt-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11px;
          margin: 6px 0 12px 0;
          color: #4a5568;
        }

        .receipt-meta span {
          font-weight: 600;
          color: #2d3748;
        }
        
        .patient-doctor-info {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin: 10px 0;
          padding: 8px 10px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
        }
        
        .info-column {
          flex: 1;
        }
        
        .info-column h4 {
          font-size: 11px;
          font-weight: bold;
          color: #2d3748;
          margin-bottom: 4px;
          text-transform: uppercase;
        }
        
        .info-item {
          margin: 3px 0;
          font-size: 11px;
        }
        
        .info-label {
          font-weight: 500;
          color: #4a5568;
        }
        
        .info-value {
          color: #2d3748;
        }
        
        .treatment-details {
          margin: 16px 0;
        }
        
        .section-title {
          font-size: 13px;
          font-weight: bold;
          color: #2d3748;
          margin: 12px 0 6px 0;
          padding-bottom: 4px;
          border-bottom: 1px solid #e2e8f0;
          text-transform: uppercase;
        }
        
        .section-title.small {
          font-size: 12px;
          margin: 10px 0 6px 0;
        }
        
        .teeth-selection {
          background: #fff;
          padding: 8px;
          border-radius: 6px;
          margin: 8px 0;
          border: 1px solid #e2e8f0;
          font-size: 12px;
          line-height: 1.25;
        }
        
        .procedures-section {
          margin: 12px 0;
        }

        .invoice-table-wrapper {
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          overflow: hidden;
        }

        .invoice-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 11px;
        }

        .invoice-table thead {
          background: #edf2f7;
        }

        .invoice-table th,
        .invoice-table td {
          padding: 8px 6px;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: top;
        }

        .invoice-table th {
          text-align: left;
          text-transform: uppercase;
          font-size: 10px;
          letter-spacing: 0.4px;
          color: #2d3748;
        }

        .procedure-name {
          font-weight: bold;
          color: #2d3748;
          font-size: 12px;
        }

        .procedure-sub {
          font-size: 10.5px;
          color: #4a5568;
          margin-top: 2px;
        }

        .procedure-notes {
          font-size: 10px;
          color: #718096;
          margin-top: 4px;
          font-style: italic;
        }

        .tax-lines {
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: 10.5px;
          color: #4a5568;
        }

        .tax-line {
          display: flex;
          justify-content: space-between;
          gap: 8px;
        }

        .procedure-discount td {
          background: #fff5f5;
          color: #c53030;
          font-size: 10.5px;
        }

        .empty-row {
          text-align: center;
          font-style: italic;
          color: #718096;
        }

        .total-section {
          margin: 14px 0 16px 0;
          border-top: 2px solid #2d3748;
          padding-top: 8px;
          display: flex;
          justify-content: flex-end;
        }

        .totals-table {
          min-width: 260px;
          font-size: 12px;
        }

        .totals-table div {
          display: flex;
          justify-content: space-between;
          margin: 4px 0;
        }

        .totals-table .total-final {
          font-size: 16px;
          font-weight: bold;
          border-top: 1px solid #2d3748;
          padding-top: 6px;
          font-family: "Courier New", monospace;
        }

        .text-right {
          text-align: right;
        }

        .text-center {
          text-align: center;
        }

        .muted {
          color: #a0aec0;
        }
        
        .notes-section {
          background: white;
          padding: 8px 0 0 0;
          border-radius: 0;
          margin: 10px 0;
          border-top: 1px solid #e2e8f0;
          font-size: 12px;
        }
        
        .expiration-section {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 8px;
          margin: 10px 0;
          text-align: center;
          font-size: 12px;
        }
        
        .expiration-expired {
          color: #e53e3e;
          font-weight: bold;
        }
        
        .expiration-warning {
          color: #d69e2e;
          font-weight: bold;
        }
        
        .expiration-valid {
          color: #38a169;
          font-weight: bold;
        }

        .status-info {
          color: #2d3748;
          font-weight: bold;
        }
        
        .footer-section {
          margin-top: 24px;
          padding-top: 12px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
        }
        
        .signature-area {
          text-align: center;
          width: 210px;
        }
        
        .signature-line {
          border-top: 2px solid #2d3748;
          margin: 28px 0 8px 0;
        }
        
        .doctor-signature {
          font-size: 12px;
          font-weight: bold;
          color: #2d3748;
        }
        
        .doctor-title {
          font-size: 11px;
          color: #4a5568;
          margin-top: 5px;
        }
        
        .print-date {
          font-size: 11px;
          color: #718096;
        }
        
        @media print {
          body { 
            margin: 8px; 
            background: white;
          }
          .receipt {
            box-shadow: none;
            border: 0;
            padding: 0;
            max-width: 100%;
          }
          .treatment-plan-header { 
            break-inside: avoid; 
          }
          .procedure-row {
            break-inside: avoid;
          }
          .total-section {
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .patient-doctor-info {
            break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <div class="receipt">
        <div class="treatment-plan-header">
          ${logoUrl ? `
          <div class="logo-section">
            <img src="${logoUrl}" alt="Logo Clínica">
          </div>
          ` : ''}

          <div class="header-content">
            <div class="clinic-name">${clinicName}</div>
            <div class="document-title">Nota de Tratamiento</div>
          </div>
        </div>

        <div class="receipt-meta">
          <div>Fecha: <span>${currentDate}</span></div>
        </div>

        <div class="patient-doctor-info">
          <div class="info-column">
            <h4>Información del Paciente</h4>
            <div class="info-item">
              <span class="info-label">Nombre:</span>
              <span class="info-value">${patientName}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Plan:</span>
              <span class="info-value">${treatmentPlan.name}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Estado del Plan:</span>
              <span class="info-value">
                ${getStatusLabel(effectiveStatus)}${effectiveStatus === 'quoted' ? ` · ${getExpirationText()}` : ''}
              </span>
            </div>
          </div>
          
          <div class="info-column">
            <h4>Información del Tratamiento</h4>
            <div class="info-item">
              <span class="info-label">Doctor:</span>
              <span class="info-value">${doctorName}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Fecha de Inicio:</span>
              <span class="info-value">${treatmentDate}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Fecha de Creación:</span>
              <span class="info-value">${createdDate}</span>
            </div>
          </div>
        </div>

        <div class="treatment-details">
          <div class="section-title small">Dientes Seleccionados</div>
          <div class="teeth-selection">
            ${selectedTeethInfo()}
          </div>

          <div class="section-title">Procedimientos</div>
          <div class="procedures-section">
            <div class="invoice-table-wrapper">
              <table class="invoice-table">
                <thead>
                  <tr>
                    <th>Procedimiento</th>
                    <th class="text-center">Cant.</th>
                    <th class="text-right">P. Unitario</th>
                    <th class="text-right">Subtotal</th>
                    <th>Impuestos</th>
                    <th class="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${proceduresList()}
                </tbody>
              </table>
            </div>
          </div>

          ${treatmentPlan.notes ? `
            <div class="section-title">Notas Adicionales</div>
            <div class="notes-section">
              ${treatmentPlan.notes}
            </div>
          ` : ''}
        </div>

        <div class="total-section">
          <div class="totals-table">
            <div>
              <span>Subtotal</span>
              <span>${formatCurrency(proceduresTotals.subtotal)}</span>
            </div>
            ${proceduresTotals.discount > 0 ? `
              <div>
                <span>Descuento</span>
                <span>-${formatCurrency(proceduresTotals.discount)}</span>
              </div>
            ` : ''}
            ${proceduresTotals.iva > 0 ? `
              <div>
                <span>IVA</span>
                <span>${formatCurrency(proceduresTotals.iva)}</span>
              </div>
            ` : ''}
            ${proceduresTotals.isr > 0 ? `
              <div>
                <span>ISR</span>
                <span>-${formatCurrency(proceduresTotals.isr)}</span>
              </div>
            ` : ''}
            <div class="total-final">
              <span>Total</span>
              <span>${formatCurrency(proceduresTotals.total)}</span>
            </div>
          </div>
        </div>

        <div class="footer-section">
          <div class="print-date">
            Impreso el ${currentDate}
          </div>
          
          <div class="signature-area">
            <div class="signature-line"></div>
            <div class="doctor-signature">${doctorName}</div>
            <div class="doctor-title">${doctorTitle}</div>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Prints a treatment plan by opening a new window with the treatment plan content
 * @param {Object} treatmentPlan - Treatment plan data
 * @param {Object} patient - Patient data
 * @param {Object} organization - Organization data (optional)
 * @param {Object} doctor - Doctor data (optional)
 * @param {Array|Object} procedureCatalog - Procedure catalog or map with tax configuration (optional)
 */
export const printTreatmentPlan = (treatmentPlan, patient, logoUrl = null, organization = null, doctor = null, procedureCatalog = null) => {
  try {
    // Generate HTML content for the treatment plan
    const printContent = generateTreatmentPlanHTML(treatmentPlan, patient, logoUrl, organization, doctor, procedureCatalog);

    // Create a new window and write the HTML content
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('No se pudo abrir la ventana de impresión. Verifica que no esté bloqueada por el navegador.');
    }

    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for the content to load, then trigger print
    printWindow.onload = () => {
      printWindow.print();
      // Close the window after printing (optional)
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    };
  } catch (error) {
    console.error('Error printing treatment plan:', error);
    throw new Error('Error al imprimir el plan de tratamiento: ' + error.message);
  }
};

export const generateTreatmentPlanPdfBlob = async (
  treatmentPlan,
  patient,
  logoUrl = null,
  organization = null,
  doctor = null,
  procedureCatalog = null
) => {
  let iframe = null;

  const waitForImages = async (doc) => {
    const images = Array.from(doc.images || []);
    if (images.length === 0) return;
    await Promise.all(images.map((img) => new Promise((resolve) => {
      if (img.complete) {
        resolve();
        return;
      }
      img.onload = resolve;
      img.onerror = resolve;
    })));
  };

  try {
    // html2canvas drops cross-origin images unless the bucket sends CORS
    // headers, so inline the logo as a data URL before rendering.
    const inlineLogoUrl = await fetchImageAsDataUrl(logoUrl);
    const printContent = generateTreatmentPlanHTML(treatmentPlan, patient, inlineLogoUrl, organization, doctor, procedureCatalog);

    iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.left = '-9999px';
    iframe.style.top = '0';
    iframe.style.width = '900px';
    iframe.style.height = '1200px';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument;
    if (!iframeDoc) {
      throw new Error('No se pudo inicializar el documento del PDF.');
    }

    iframeDoc.open();
    iframeDoc.write(printContent);
    iframeDoc.close();

    await new Promise((resolve) => setTimeout(resolve, 50));
    await waitForImages(iframeDoc);

    const target = iframeDoc.querySelector('.receipt') || iframeDoc.body;
    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: iframeDoc.documentElement.scrollWidth,
      windowHeight: iframeDoc.documentElement.scrollHeight,
      scrollX: 0,
      scrollY: 0
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    return pdf.output('blob');
  } catch (error) {
    console.error('Error generating treatment plan PDF:', error);
    throw new Error('Error al generar el PDF del plan de tratamiento');
  } finally {
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  }
};
